import React, { useState } from 'react';
import { supabase } from '../../utils/supabase';
import { getApiUrl } from '../../utils/apiConfig';
import '../../styles/student/MainContent.css';
import { BarChart, Bar, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import FunFact from './FunFact';
import ReviewQuestions from './ReviewQuestions';
import { useSubjectScores } from '../../hooks/useSubjectScores';

// Default subject data (fallback)

const emotionImageBase = (process.env.PUBLIC_URL || '') + '/Emotion/';
const emotions = [
  { label: 'Sad', image: 'Sad.png', color: '#90caf9', message: "It's okay to feel sad. Be kind to yourself." },
  { label: 'Tired', image: 'Tired.png', color: '#90caf9', message: "It's okay to feel exhausted. Take a moment to rest." },
  { label: 'Lonely', image: 'Lonely.png', color: '#ff8a80', message: "It's okay to feel lonely. Reach out to Itzy." },
  { label: 'Bored', image: 'Bored.png', color: '#bdbdbd', message: "Boredom can be a sign to try something new!" },
  { label: 'Happy', image: 'Happy.png', color: '#9370db', message: "Yay! Keep spreading your happiness!" },
  { label: 'Neutral', image: 'Neutral.png', color: '#ffe082', message: "Feeling neutral is perfectly fine." },
  { label: 'Anxious', image: 'Anxious.png', color: '#4fc3f7', message: "Feeling anxious? Take a moment to pause and relax." },
  { label: 'Stressed', image: 'Stressed.png', color: '#ffb74d', message: "It's okay to feel stressed. Take a moment to relax." },
  { label: 'Angry', image: 'Angry.png', color: '#ff6b6b', message: "It's okay to feel angry. Try some deep breaths." },
  { label: 'Fearful', image: 'Fearful.png', color: '#9575cd', message: "It's normal to feel fear. You're not alone." },
];


const MainContent = ({ selectedNav }) => {
  const [selectedEmotion, setSelectedEmotion] = useState(0);
  const [thoughts, setThoughts] = useState("");
  const [hoveredEmotion, setHoveredEmotion] = useState(null);
  
  // Use the custom hook for subject scores
  const { subjectData, loadingSubjects } = useSubjectScores();

  const handleEmojiClick = (idx) => {
    setSelectedEmotion(idx);
  };

  const handleThoughtsChange = (e) => {
    setThoughts(e.target.value);
  };

  const handleSubmit = async () => {
    if (!thoughts.trim()) {
      return;
    }

    try {
      // Get the current Supabase session to access the JWT token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session || !session.access_token) {
        console.error('No Supabase session found:', sessionError);
        alert('Please log in to submit your feedback');
        return;
      }
      
      const token = session.access_token;

      console.log('🔄 Submitting feedback with emotion:', emotions[selectedEmotion].label);

      const response = await fetch(getApiUrl('emotion-feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          after_reading_emotions: emotions[selectedEmotion].label,
          feedback: thoughts
        })
      });

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        console.log('✅ Emotion feedback submitted successfully');
        alert('Thank you for sharing your thoughts!');
        setThoughts(""); // Clear the textarea after successful submission
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to submit feedback:', errorData);
        console.error('❌ Status:', response.status);
        alert(`Failed to submit feedback: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error submitting feedback:', error);
      alert('An error occurred. Please try again.');
    }
  };







  return (
    <main className="student-main-content">
      <div className="student-content-grid">
        <div className="content-box">
          <div className="emotion-checkin-container">
            <h2 className="emotion-title">HOW ARE YOU FEELING TODAY?</h2>
            <div className="emotion-slider-row">
              {emotions.map((emotion, idx) => (
                <div
                  key={emotion.label}
                  className={`emotion-emoji-label${selectedEmotion === idx ? ' selected' : ''}`}
                  onClick={() => handleEmojiClick(idx)}
                  onMouseEnter={() => setHoveredEmotion(idx)}
                  onMouseLeave={() => setHoveredEmotion(null)}
                >
                  <div className="emotion-emoji">
                    <img
                      src={`${emotionImageBase}${emotion.image}`}
                      alt={emotion.label}
                      className="emotion-emoji-img"
                    />
                  </div>
                  {/* Tooltip showing emotion label */}
                  {hoveredEmotion === idx && (
                    <div className="emotion-tooltip">{emotion.label}</div>
                  )}
                </div>
              ))}
            </div>
            {/* Slider removed - emojis are now clickable */}
            {/* Live feedback message */}
            <div className="emotion-feedback">
              <strong>{emotions[selectedEmotion].message}</strong>
            </div>
            <div className="emotion-thoughts-box">
              <label htmlFor="thoughts-textarea" className="emotion-thoughts-label">
                What is on your mind today?
              </label>
              <div className="emotion-thoughts-container">
                <textarea
                  id="thoughts-textarea"
                  className="emotion-thoughts-textarea"
                  value={thoughts}
                  onChange={handleThoughtsChange}
                  placeholder="Type your thoughts..."
                  rows={2}
                />
                <button 
                  className="emotion-thoughts-submit-btn app-btn" 
                  onClick={handleSubmit}
                  disabled={!thoughts.trim()}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="content-box">
          <div className="subject-ranking-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <h3 className="subject-ranking-title">QUIZ SCORES</h3>
            
            {loadingSubjects ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                flex: 1,
                color: '#666',
                fontSize: '16px'
              }}>
                Loading your quiz scores...
              </div>
            ) : (
              <div style={{ width: '100%', flex: 1, minHeight: '200px', marginTop: '20px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={subjectData} margin={{ top: 20, right: 30, left: 10, bottom: 30 }}>
                    <XAxis type="number" domain={[0, 100]} label={{ value: 'Average Score', position: 'insideBottom', offset: -15, style: { textAnchor: 'middle', fill: '#000000' } }} tick={{ fill: '#000000', fontSize: 14, fontWeight: 'bold' }} />
                    <YAxis type="category" dataKey="subject" width={100} tick={{ fill: '#000000', fontSize: 14, fontWeight: 'bold' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        color: '#000000'
                      }}
                      cursor={false}
                      formatter={(value, name, props) => [
                        `${value}% (${props.payload.total_quizzes} quizzes, ${props.payload.accuracy}% accuracy)`,
                        'Average Score'
                      ]}
                      labelFormatter={(label) => `Subject: ${label}`}
                    />
                    <Bar dataKey="score" name="Average Score" maxBarSize={48}>
                      {subjectData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || `hsl(${index * 120}, 70%, 50%)`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            
            {!loadingSubjects && subjectData.every(subject => subject.score === 0) && (
              <div style={{ 
                textAlign: 'center', 
                color: '#666', 
                marginTop: '20px',
                fontSize: '14px',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                No quiz data available yet. Take some quizzes to see your subject rankings!
              </div>
            )}
          </div>
        </div>
        <div className="content-box">
          <FunFact />
        </div>
        <div className="content-box">
          <ReviewQuestions />
        </div>
      </div>
    </main>
  );
};

export default MainContent; 